import api from '../../../services/api'

export interface CreatePaymentIntentRequest {
  amount: number
  currency?: string
  description?: string
  metadata?: Record<string, string>
}

export interface CreatePaymentIntentResponse {
  clientSecret: string
  paymentIntentId: string
}

export interface PixPaymentRequest {
  amount: number
  orderId?: string
  description?: string
  customerEmail?: string
  customerName?: string
}

export interface PixPaymentResponse {
  paymentIntentId: string
  clientSecret: string | null
  pixCode: string
  qrCodeUrl: string
  expiresAt: string
  amount: number
  currency: string
}

export interface BoletoPaymentRequest {
  orderId?: string
  amount: number
  description?: string
  customerEmail: string
  customerName: string
  customerDocument: string
  dueDate?: string
}

export interface BoletoPaymentResponse {
  boletoUrl: string
  boletoBarcode: string
  boletoDigitableLine: string
  expiresAt: string
  paymentId: string
}

export interface CreditCardData {
  number: string
  holderName: string
  expiryMonth: string
  expiryYear: string
  cvv: string
}

export interface CreditCardPaymentRequest {
  orderId: string
  amount: number
  installments: number
  creditCardToken?: string
  cardData?: CreditCardData
}

export interface CreditCardPaymentResponse {
  transactionId: string
  status: string
  authorizationCode: string | null
  installments: number
  creditCardToken: string | null
}

/**
 * Cria um Payment Intent do Asaas para pagamentos com cartão
 */
export const createPaymentIntent = async (
  data: CreatePaymentIntentRequest
): Promise<CreatePaymentIntentResponse> => {
  try {
    const response = await api.post<CreatePaymentIntentResponse>(
      '/payment/create-intent',
      {
        amount: data.amount, // Asaas espera valor em reais
        currency: data.currency || 'BRL',
        description: data.description,
        metadata: data.metadata
      }
    )
    const payload = response.data as unknown
    if (
      typeof payload === 'object' &&
      payload !== null &&
      'data' in payload &&
      typeof (payload as { data?: unknown }).data === 'object' &&
      (payload as { success?: boolean }).success !== false
    ) {
      const dataPayload = (payload as { data: CreatePaymentIntentResponse }).data
      if (dataPayload.clientSecret) {
        return dataPayload
      }
    }
    throw new Error('Resposta inválida ao criar Payment Intent')
  } catch (error) {
    console.error('Erro ao criar Payment Intent:', error)
    throw new Error('Erro ao inicializar pagamento com cartão')
  }
}

/**
 * Cria um pagamento PIX
 */
export const createPixPayment = async (
  data: PixPaymentRequest
): Promise<PixPaymentResponse> => {
  try {
    const response = await api.post<{
      success?: boolean
      data?: PixPaymentResponse
    } | PixPaymentResponse>(
      '/payment/pix',
      {
        amount: Number(data.amount.toFixed(2)),
        orderId: data.orderId,
        description: data.description,
        customerEmail: data.customerEmail,
        customerName: data.customerName
      }
    )
    const payload = response.data as
      | PixPaymentResponse
      | {
          success?: boolean
          data?: PixPaymentResponse
        }

    let dataPayload: PixPaymentResponse | undefined

    if (payload && typeof payload === 'object' && 'data' in payload) {
      dataPayload = (payload as { data?: PixPaymentResponse }).data
    } else if (
      payload &&
      typeof payload === 'object' &&
      'paymentIntentId' in payload
    ) {
      dataPayload = payload as PixPaymentResponse
    }

    if (dataPayload && dataPayload.paymentIntentId && dataPayload.pixCode) {
      return {
        ...dataPayload,
        currency: (dataPayload.currency ?? 'BRL').toUpperCase(),
        amount: Number(dataPayload.amount)
      }
    }

    throw new Error('Resposta inválida ao gerar pagamento PIX')
  } catch (error) {
    console.error('Erro ao criar pagamento PIX:', error)
    throw new Error('Erro ao gerar código PIX')
  }
}

/**
 * Cria um boleto bancário
 */
export const createBoletoPayment = async (
  data: BoletoPaymentRequest
): Promise<BoletoPaymentResponse> => {
  try {
    const response = await api.post<
      | {
          success?: boolean
          data?: BoletoPaymentResponse
        }
      | BoletoPaymentResponse
    >('/payment/boleto', data)
    
    const payload = response.data as
      | BoletoPaymentResponse
      | {
          success?: boolean
          data?: BoletoPaymentResponse
        }

    let dataPayload: BoletoPaymentResponse | undefined

    if (payload && typeof payload === 'object' && 'data' in payload) {
      dataPayload = (payload as { data?: BoletoPaymentResponse }).data
    } else if (
      payload &&
      typeof payload === 'object' &&
      'boletoUrl' in payload
    ) {
      dataPayload = payload as BoletoPaymentResponse
    }

    if (dataPayload && dataPayload.boletoUrl) {
      return dataPayload
    }

    throw new Error('Resposta inválida ao gerar boleto bancário')
  } catch (error) {
    console.error('Erro ao criar boleto:', error)
    throw new Error('Erro ao gerar boleto bancário')
  }
}

/**
 * Processa um pagamento com cartão de crédito
 */
export const processCreditCardPayment = async (
  data: CreditCardPaymentRequest
): Promise<CreditCardPaymentResponse> => {
  try {
    const response = await api.post<
      | {
          success?: boolean
          data?: CreditCardPaymentResponse
        }
      | CreditCardPaymentResponse
    >('/payment/credit-card', data)

    const payload = response.data as
      | CreditCardPaymentResponse
      | {
          success?: boolean
          data?: CreditCardPaymentResponse
        }

    let dataPayload: CreditCardPaymentResponse | undefined

    if (payload && typeof payload === 'object' && 'data' in payload) {
      dataPayload = (payload as { data?: CreditCardPaymentResponse }).data
    } else if (payload && typeof payload === 'object' && 'transactionId' in payload) {
      dataPayload = payload as CreditCardPaymentResponse
    }

    if (dataPayload && dataPayload.transactionId && dataPayload.status) {
      return dataPayload
    }

    throw new Error('Resposta inválida ao processar pagamento com cartão')
  } catch (error) {
    console.error('Erro ao processar pagamento com cartão:', error)
    throw new Error('Erro ao processar pagamento com cartão')
  }
}

/**
 * Verifica o status de um pagamento
 */
export interface PaymentStatusResponse {
  id: string
  status: string
  amount: number
  currency: string
  createdAt: string
}

type RawPaymentStatus = {
  id: string
  status: string
  amount: number
  currency: string
  created: number
}

export const checkPaymentStatus = async (
  paymentId: string
): Promise<PaymentStatusResponse> => {
  try {
    const response = await api.get(`/payment/status/${paymentId}`)
    const payload = response.data as
      | {
          success?: boolean
          data?: RawPaymentStatus
        }
      | RawPaymentStatus

    let dataPayload: RawPaymentStatus | undefined

    if (payload && typeof payload === 'object' && 'data' in payload) {
      dataPayload = (payload as { data?: RawPaymentStatus }).data
    } else if (payload && typeof payload === 'object' && 'status' in payload) {
      dataPayload = payload as RawPaymentStatus
    }

    if (dataPayload) {
      const createdSeconds = dataPayload.created ?? Math.floor(Date.now() / 1000)
      return {
        id: dataPayload.id,
        status: dataPayload.status,
        amount: Number(dataPayload.amount ?? 0) / 100,
        currency: (dataPayload.currency ?? 'BRL').toUpperCase(),
        createdAt: new Date(createdSeconds * 1000).toISOString()
      }
    }

    throw new Error('Resposta inválida ao consultar status do pagamento')
  } catch (error) {
    console.error('Erro ao verificar status do pagamento:', error)
    throw new Error('Erro ao verificar status do pagamento')
  }
}

/**
 * Confirma o pagamento e atualiza o status do pedido
 */
export const confirmPaymentAndUpdateOrder = async (
  paymentIntentId: string
): Promise<{ success: boolean; status: string; orderUpdated: boolean }> => {
  try {
    // Codificar o ID para garantir que caracteres especiais sejam tratados corretamente
    const encodedId = encodeURIComponent(paymentIntentId)
    const response = await api.post<{
      success?: boolean
      data?: {
        success: boolean
        status: string
        orderUpdated: boolean
      }
    }>(`/payment/confirm/${encodedId}`)

    const payload = response.data as
      | {
          success?: boolean
          data?: {
            success: boolean
            status: string
            orderUpdated: boolean
          }
        }
      | {
          success: boolean
          status: string
          orderUpdated: boolean
        }

    if (payload && typeof payload === 'object' && 'data' in payload && payload.data) {
      return {
        success: payload.data.success ?? true,
        status: payload.data.status,
        orderUpdated: payload.data.orderUpdated
      }
    } else if (payload && typeof payload === 'object' && 'status' in payload) {
      return payload as { success: boolean; status: string; orderUpdated: boolean }
    }

    throw new Error('Resposta inválida ao confirmar pagamento')
  } catch (error) {
    console.error('Erro ao confirmar pagamento:', error)
    throw new Error('Erro ao confirmar pagamento e atualizar pedido')
  }
}

/**
 * Cancela um pagamento pendente
 */
export const cancelPayment = async (paymentId: string): Promise<void> => {
  try {
    await api.post(`/payment/cancel/${paymentId}`)
  } catch (error) {
    console.error('Erro ao cancelar pagamento:', error)
    throw new Error('Erro ao cancelar pagamento')
  }
}

/**
 * Simula um pagamento PIX (apenas em desenvolvimento)
 */
export const simulatePixPayment = async (
  pixId: string
): Promise<PaymentStatusResponse> => {
  try {
    const response = await api.post<{
      success?: boolean
      data?: PaymentStatusResponse
    } | PaymentStatusResponse>(
      `/payment/pix/simulate/${pixId}`
    )
    const payload = response.data as
      | PaymentStatusResponse
      | {
          success?: boolean
          data?: PaymentStatusResponse
        }

    let dataPayload: PaymentStatusResponse | undefined

    if (payload && typeof payload === 'object' && 'data' in payload) {
      dataPayload = (payload as { data?: PaymentStatusResponse }).data
    } else if (
      payload &&
      typeof payload === 'object' &&
      'id' in payload
    ) {
      dataPayload = payload as PaymentStatusResponse
    }

    if (dataPayload) {
      return dataPayload
    }

    throw new Error('Resposta inválida ao simular pagamento PIX')
  } catch (error) {
    console.error('Erro ao simular pagamento PIX:', error)
    throw new Error('Erro ao simular pagamento PIX')
  }
}

/**
 * Busca detalhes de pagamento por orderId
 */
export const getPaymentDetailsByOrderId = async (
  orderId: string
): Promise<any> => {
  try {
    const response = await api.get(`/payment/order/${orderId}/details`)
    const payload = response.data as any
    if (payload && payload.success) {
      return payload.data
    }
    throw new Error('Falha ao obter detalhes do pagamento')
  } catch (error) {
    console.error('Erro ao buscar detalhes do pagamento:', error)
    throw new Error('Erro ao buscar detalhes do pagamento')
  }
}

